import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

const API_BASE = '/api/sites'

interface AvailableSection {
  key: string
  label: string
}

interface ReportTemplate {
  name: string
  description: string
  sections: string[]
}

export interface ReportConfig {
  id: string
  site_id: string
  name: string
  sections: string[]
  custom_text: string | null
  created_at: string
  updated_at: string
}

interface ReportConfigsResponse {
  configs: ReportConfig[]
  templates: Record<string, ReportTemplate>
  availableSections: AvailableSection[]
}

export function useReportConfigs(siteId: string) {
  return useQuery({
    queryKey: ['report-configs', siteId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/${siteId}/report-configs`, { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to fetch report configs')
      return res.json() as Promise<ReportConfigsResponse>
    },
    enabled: !!siteId,
  })
}

export function useCreateReportConfig(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { name: string; sections: string[]; customText?: string }) => {
      const res = await fetch(`${API_BASE}/${siteId}/report-configs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('Failed to create report config')
      return res.json() as Promise<{ id: string }>
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report-configs', siteId] }),
  })
}

export function useUpdateReportConfig(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ configId, ...data }: { configId: string; name: string; sections: string[]; customText?: string }) => {
      const res = await fetch(`${API_BASE}/${siteId}/report-configs/${configId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('Failed to update report config')
      return res.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report-configs', siteId] }),
  })
}

export function useDeleteReportConfig(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (configId: string) => {
      const res = await fetch(`${API_BASE}/${siteId}/report-configs/${configId}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to delete report config')
      return res.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report-configs', siteId] }),
  })
}

export function useGenerateReport(siteId: string) {
  return useMutation({
    mutationFn: async (params: { configId?: string; template?: string; sections?: string[] }) => {
      const res = await fetch(`${API_BASE}/${siteId}/report/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      })
      if (!res.ok) throw new Error('Failed to generate report')
      // Return the HTML as a blob URL for preview
      const html = await res.text()
      const blob = new Blob([html], { type: 'text/html' })
      return URL.createObjectURL(blob)
    },
  })
}
